/**
 * Service to save scanned albums to Jookbox
 * Automatically imports albums upon successful barcode scan
 */

import { db, storage } from '../../firebase/config';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

class BarcodeSaveService {
  /**
   * Check if album already exists in albums-flat
   * Public method that can be called from outside
   */
  async checkDuplicate(artist, albumName) {
    try {
      const existingQuery = query(
        collection(db, 'albums-flat'),
        where('artist', '==', artist),
        where('album', '==', albumName)
      );
      
      const existingDocs = await getDocs(existingQuery);
      return !existingDocs.empty;
    } catch (error) {
      console.error('Error checking for duplicate:', error);
      return false; // If error, allow save to proceed
    }
  }

  /**
   * Download image from URL and convert to Blob
   */
  async downloadImageAsBlob(imageUrl) {
    try {
      const response = await fetch(imageUrl, {
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Error downloading image:', error);
      throw error;
    }
  }

  /**
   * Upload cover art to Firebase Storage
   */
  async uploadCoverArt(imageBlob, storagePath) {
    try {
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, imageBlob, {
        contentType: 'image/jpeg',
      });

      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          null,
          (error) => reject(error),
          () => resolve()
        );
      });

      // Get download URL (optional - we use the path in Firestore)
      // const downloadURL = await getDownloadURL(storageRef);
      return storagePath;
    } catch (error) {
      console.error('Error uploading cover art:', error);
      throw error;
    }
  }

  /**
   * Normalize text (remove accents, Title Case)
   */
  normalizeText(text) {
    if (!text) return '';
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Save scanned album to Jookbox
   */
  async saveScannedAlbum({
    barcode,
    release, // MusicBrainz release data
    spotifyAlbum, // Spotify album data
    albumData, // Full album data with tracks
    userId,
  }) {
    try {
      const artist = release.artist || spotifyAlbum.artist;
      const albumName = release.title || albumData.album.name;
      
      // Check for duplicate
      const isDuplicate = await this.checkDuplicate(artist, albumName);
      if (isDuplicate) {
        console.log(`⏭️ Album already exists: ${artist} - ${albumName}`);
        return { success: false, reason: 'duplicate' };
      }

      // Extract release year from date
      let releaseYear = null;
      if (release.date) {
        const yearMatch = release.date.match(/^(\d{4})/);
        if (yearMatch) {
          releaseYear = parseInt(yearMatch[1]);
        }
      }

      // Create songs array in Jookbox format
      const songs = albumData.tracks.map((track, index) => ({
        Id: track.id || `spotify_${index}`,
        Title: track.name,
        DisplayTitle: this.normalizeText(track.name),
        trackNumber: index + 1,
        duration: track.duration || null,
        spotifyUri: track.uri,
        spotifyId: track.id,
        artist: track.artist || artist,
        album: albumName,
        year: releaseYear,
        metadataSource: 'barcode-scan',
        metadataStatus: 'complete',
      }));

      // Download and upload cover art
      let coverUrl = null;
      // Get image URL - spotifyAlbum from search has image property, albumData.album also has image property
      const coverImageUrl = spotifyAlbum.image || albumData.album.image;
      
      if (coverImageUrl) {
        try {
          // Sanitize artist and album name for storage path
          const sanitizePath = (str) => {
            return str.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, ' ').trim();
          };
          
          const sanitizedArtist = sanitizePath(artist);
          const sanitizedAlbum = sanitizePath(albumName);
          const storagePath = `Albums/English/Various/Scanned/${sanitizedArtist}/${sanitizedAlbum}/cover.jpg`;
          
          console.log('📥 Downloading cover art...');
          const imageBlob = await this.downloadImageAsBlob(coverImageUrl);
          
          console.log('📤 Uploading cover art to Storage...');
          coverUrl = await this.uploadCoverArt(imageBlob, storagePath);
          console.log('✅ Cover art uploaded:', coverUrl);
        } catch (coverError) {
          console.warn('⚠️ Failed to download/upload cover art:', coverError);
          // Continue without cover art
        }
      }

      // Create album document
      const albumDoc = {
        type: 'Albums',
        language: 'English',
        genre: 'Various',
        subgenre: 'Scanned',
        artist: artist,
        album: albumName,
        albumName: albumName,
        artistName: artist,
        songs: songs,
        coverUrl: coverUrl,
        barcode: barcode,
        albumMbid: release.mbid || null,
        releaseYear: releaseYear,
        spotifyAlbumId: spotifyAlbum.id,
        spotifyAlbumUri: `spotify:album:${spotifyAlbum.id}`,
        storageStatus: 'spotify', // Indicates from Spotify, not local storage
        source: 'barcode-scan',
        scannedBy: userId,
        scannedAt: new Date(),
        createdAt: new Date(),
        metadataSource: 'barcode-scan',
        metadataStatus: 'complete',
      };

      // Save to Firestore
      console.log('💾 Saving album to Firestore...');
      const docRef = await addDoc(collection(db, 'albums-flat'), albumDoc);
      console.log('✅ Album saved to Firestore:', docRef.id);

      return {
        success: true,
        docId: docRef.id,
        album: albumName,
        artist: artist,
      };
    } catch (error) {
      console.error('❌ Error saving scanned album:', error);
      throw error;
    }
  }
}

export const barcodeSaveService = new BarcodeSaveService();

